import { FC, useState, useMemo, useCallback, useEffect } from "react"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import {
  fetchAnalytics,
  selectAnalytics,
} from "../../app/reducers/analyticsSlice"
import {
  selectWbReports,
  updateReportState,
} from "../../app/reducers/reportsSlice"
import { STATUS } from "../../utils/const"
import { FinReport } from "../reports/wb/FinReport"
import { SamsonReport } from "../reports/samson/SamsonReport"
import { ProfitSection } from "./sections/ProfitSection"
import { AbcSection } from "./sections/AbcSection"
import { ReturnsSection } from "./sections/ReturnsSection"
import { PriceVsSalesSection } from "./sections/PriceVsSalesSection"
import { fmt, fmtRub, fmtPct } from "./utils/formatters"
import "./WbAnalytics.css"
import { Checkbox, Button, TextField } from "@radix-ui/themes"

type Tab =
  | "profit"
  | "abc"
  | "returns"
  | "priceVsSales"
  | "wb-reports"
  | "samson-reports"

const TAB_LABELS: Record<Tab, string> = {
  profit: "Прибыльность",
  abc: "ABC-анализ",
  returns: "Возвраты",
  priceVsSales: "Цена vs Продажи",
  "wb-reports": "Отчёты WB",
  "samson-reports": "Отчёты Самсон",
}

export const WbAnalytics: FC = () => {
  const dispatch = useAppDispatch()
  const { data, status } = useAppSelector(selectAnalytics)
  const [dateFrom, setDateFrom] = useState("")
  const [dateTo, setDateTo] = useState("")
  const [excludeNoCost, setExcludeNoCost] = useState(true)
  const [activeTab, setActiveTab] = useState<Tab>("profit")
  const reports = useAppSelector(selectWbReports)
  const { minDate, maxDate } = useMemo(() => {
    const dates = reports
      .filter((r) => r.isDaily && r.status === STATUS.SUCCESS)
      .map((r) => r.dateFrom)
      .sort()
    return { minDate: dates[0] ?? "", maxDate: dates[dates.length - 1] ?? "" }
  }, [reports])

  useEffect(() => {
    dispatch(updateReportState())
  }, [dispatch])

  const handleLoad = useCallback(() => {
    if (dateFrom && dateTo)
      dispatch(fetchAnalytics({ dateFrom, dateTo, excludeNoCost }))
  }, [dispatch, dateFrom, dateTo, excludeNoCost])

  const isLoading = status === STATUS.IN_PROGRESS
  const s = data?.summary
  const isReportTab =
    activeTab === "wb-reports" || activeTab === "samson-reports"

  return (
    <div className="analytics-root">
      {/* ── Tab bar — always visible ── */}
      <div className="analytics-tabs" style={{ marginBottom: 20 }}>
        {(["profit", "abc", "returns", "priceVsSales"] as Tab[]).map((tab) => (
          <Button asChild key={tab} variant="ghost">
            <button
              className={`analytics-tab ${activeTab === tab ? "active" : ""}`}
              onClick={() => setActiveTab(tab)}
            >
              {TAB_LABELS[tab]}
            </button>
          </Button>
        ))}
        <span className="analytics-tabs-divider" />
        {(["wb-reports", "samson-reports"] as Tab[]).map((tab) => (
          <Button asChild key={tab} variant="ghost">
            <button
              className={`analytics-tab ${activeTab === tab ? "active" : ""}`}
              onClick={() => setActiveTab(tab)}
            >
              {TAB_LABELS[tab]}
            </button>
          </Button>
        ))}
      </div>

      {/* ── Controls — only for analytics tabs ── */}
      {!isReportTab && (
        <div className="analytics-controls">
          <label>
            С
            <TextField.Root
              type="date"
              size="3"
              value={dateFrom}
              min={minDate}
              max={dateTo || maxDate}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </label>
          <label>
            по
            <TextField.Root
              type="date"
              size="3"
              value={dateTo}
              min={dateFrom || minDate}
              max={maxDate}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </label>
          <label
            className={`analytics-toggle ${excludeNoCost ? "active" : ""}`}
          >
            <Checkbox
              checked={excludeNoCost}
              onCheckedChange={(value) => setExcludeNoCost(value === true)}
            />
            Только с закупочной ценой
          </label>
          <Button
            onClick={handleLoad}
            disabled={isLoading}
            style={{
              padding: "7px 22px",
              borderRadius: 6,
              border: "none",
              background: isLoading ? "#adb5bd" : "#228be6",
              color: "#fff",
              cursor: isLoading ? "default" : "pointer",
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            {isLoading ? "Загрузка…" : "Показать"}
          </Button>
        </div>
      )}

      {/* ── Report tabs ── */}
      {activeTab === "wb-reports" && (
        <div className="chart-wrap">
          <FinReport />
        </div>
      )}
      {activeTab === "samson-reports" && (
        <div className="chart-wrap">
          <SamsonReport />
        </div>
      )}

      {/* ── Analytics tabs ── */}
      {!isReportTab && (
        <>
          {isLoading && (
            <div className="analytics-loading">Загрузка аналитики…</div>
          )}
          {status === STATUS.ERROR && (
            <div className="analytics-empty">Ошибка загрузки данных</div>
          )}
          {!data && !isLoading && status !== STATUS.ERROR && (
            <div className="analytics-empty">
              Выберите период и нажмите «Показать»
            </div>
          )}

          {s && data && (
            <>
              {/* ── Основные показатели ── */}
              <div className="summary-grid">
                <div className="summary-card">
                  <div className="summary-card__label">Выручка</div>
                  <div className="summary-card__value green">
                    {fmtRub(s.totalRevenue)}
                  </div>
                </div>
                <div className="summary-card">
                  <div className="summary-card__label">К выплате (продажи)</div>
                  <div className="summary-card__value blue">
                    {fmtRub(s.totalPayout)}
                  </div>
                </div>
                <div className="summary-card">
                  <div className="summary-card__label">
                    Удержано за возвраты
                  </div>
                  <div className="summary-card__value red">
                    {fmtRub(s.totalReturnPayout)}
                  </div>
                </div>
                <div className="summary-card">
                  <div className="summary-card__label">
                    Компенсации/корректировки
                  </div>
                  <div className="summary-card__value">
                    <span
                      style={{
                        color:
                          s.totalCompensationPayout >= 0
                            ? "#1a7f44"
                            : "#c92a2a",
                      }}
                    >
                      {fmtRub(s.totalCompensationPayout)}
                    </span>
                  </div>
                </div>
                <div className="summary-card">
                  <div className="summary-card__label">Себестоимость</div>
                  <div className="summary-card__value">
                    {s.salesCountWithCost > 0 ? fmtRub(s.totalCost) : "—"}
                    {s.salesCountWithCost > 0 && (
                      <span className="sub">
                        {s.salesCountWithCost}/{s.salesCount} шт
                      </span>
                    )}
                  </div>
                </div>
                <div className="summary-card">
                  <div className="summary-card__label">Валовая прибыль</div>
                  <div
                    className="summary-card__value"
                    style={{
                      color:
                        s.salesCountWithCost > 0
                          ? s.totalProfit >= 0
                            ? "#1a7f44"
                            : "#c92a2a"
                          : "var(--gray-8)",
                      fontWeight: 600,
                    }}
                  >
                    {s.salesCountWithCost > 0 ? fmtRub(s.totalProfit) : "—"}
                    {s.salesCountWithCost > 0 && (
                      <span className="sub">{fmtPct(s.avgMargin)}</span>
                    )}
                  </div>
                </div>
                <div
                  className="summary-card"
                  style={{ border: "2px solid #e64980", background: "#fff0f6" }}
                >
                  <div className="summary-card__label">Чистая прибыль</div>
                  <div
                    className="summary-card__value"
                    style={{
                      color: s.netProfit >= 0 ? "#1a7f44" : "#c92a2a",
                      fontWeight: 700,
                    }}
                  >
                    {fmtRub(s.netProfit)}
                    <span className="sub">{fmtPct(s.netMargin)}</span>
                  </div>
                </div>
                <div className="summary-card">
                  <div className="summary-card__label">Продажи</div>
                  <div className="summary-card__value">
                    {fmt(s.salesCount)} <span className="sub">шт</span>
                  </div>
                </div>
              </div>

              {/* ── Расходы ── */}
              <div className="summary-section-label">Расходы и удержания</div>
              <div className="summary-grid">
                <div className="summary-card">
                  <div className="summary-card__label">Комиссия WB</div>
                  <div className="summary-card__value orange">
                    {fmtRub(s.totalCommission)}
                  </div>
                </div>
                <div className="summary-card">
                  <div className="summary-card__label">Эквайринг</div>
                  <div className="summary-card__value orange">
                    {fmtRub(s.totalAcquiring)}
                  </div>
                </div>
                <div className="summary-card">
                  <div className="summary-card__label">Логистика</div>
                  <div className="summary-card__value orange">
                    {fmtRub(s.totalLogistics)}
                  </div>
                </div>
                <div className="summary-card">
                  <div className="summary-card__label">Хранение</div>
                  <div className="summary-card__value red">
                    {fmtRub(s.totalStorage)}
                  </div>
                </div>
                <div className="summary-card">
                  <div className="summary-card__label">Штрафы</div>
                  <div className="summary-card__value red">
                    {fmtRub(s.totalPenalty)}
                  </div>
                </div>
                <div className="summary-card">
                  <div className="summary-card__label">Удержания</div>
                  <div className="summary-card__value red">
                    {fmtRub(s.totalDeduction)}
                  </div>
                </div>
                <div className="summary-card">
                  <div className="summary-card__label">Приёмка</div>
                  <div className="summary-card__value red">
                    {fmtRub(s.totalAcceptance)}
                  </div>
                </div>
                <div className="summary-card">
                  <div className="summary-card__label">Доп. логистика</div>
                  <div className="summary-card__value red">
                    {fmtRub(s.totalRebillLogistic)}
                  </div>
                </div>
                <div className="summary-card">
                  <div className="summary-card__label">Корректировки ВВ</div>
                  <div
                    className="summary-card__value"
                    style={{
                      color:
                        s.totalAdditionalPayment >= 0 ? "#1a7f44" : "#c92a2a",
                    }}
                  >
                    {fmtRub(s.totalAdditionalPayment)}
                  </div>
                </div>
                <div className="summary-card">
                  <div className="summary-card__label">Возмещение ПВЗ</div>
                  <div
                    className="summary-card__value"
                    style={{
                      color: s.totalPpvzReward > 0 ? "#1a7f44" : "inherit",
                    }}
                  >
                    {fmtRub(s.totalPpvzReward)}
                  </div>
                </div>
                <div className="summary-card">
                  <div className="summary-card__label">
                    Программа лояльности
                  </div>
                  <div className="summary-card__value red">
                    {fmtRub(s.totalCashbackCommission)}
                  </div>
                </div>
                <div className="summary-card">
                  <div className="summary-card__label">Возвраты</div>
                  <div className="summary-card__value red">
                    {fmt(s.returnsCount)}{" "}
                    <span className="sub">шт ({s.returnRate}%)</span>
                  </div>
                </div>
              </div>

              {activeTab === "profit" && (
                <ProfitSection data={data.dailyDynamics} />
              )}
              {activeTab === "abc" && <AbcSection items={data.abcAnalysis} />}
              {activeTab === "returns" && (
                <ReturnsSection items={data.returnsAnalysis} />
              )}
              {activeTab === "priceVsSales" && (
                <PriceVsSalesSection items={data.priceVsSales} />
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}
